/**
 * Client helpers for the Terms/Privacy clickwrap gate.
 *
 * The server row (POST /api/terms/accept) is the AUTHORITATIVE legal evidence; localStorage here
 * only suppresses re-prompting. We therefore NEVER write the localStorage key until the server has
 * returned 2xx (see TermsGate) — a failed/offline POST must keep the user gated so no one passes
 * through without an evidence row.
 */
import { TERMS_VERSION, PRIVACY_VERSION } from '@parse/shared'

const SESSION_KEY = 'parse_terms_session_id'

/** Combined version tag; a bump of either constant changes the key → re-prompts everyone. */
export function acceptanceVersionTag(): string {
  return `t${TERMS_VERSION}_p${PRIVACY_VERSION}`
}

/**
 * Stable client session id (UUID) so a pre-connect acceptance has a durable key. Generated once and
 * persisted; mirrors the crypto.randomUUID() usage in burnStorage.ts.
 */
export function getOrCreateSessionId(): string {
  let id = ''
  try {
    id = localStorage.getItem(SESSION_KEY) ?? ''
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(SESSION_KEY, id)
    }
  } catch {
    // localStorage unavailable (private mode / disabled) — fall back to an ephemeral id.
    id = crypto.randomUUID()
  }
  return id
}

/** localStorage key for the current versions, scoped to the wallet (if connected) else the session. */
export function acceptanceStorageKey(walletOrSession: string): string {
  return `parse_terms_accepted_${walletOrSession}_${acceptanceVersionTag()}`
}

/** True iff this wallet/session has already accepted the CURRENT versions on this device. */
export function hasAcceptedCurrent(walletOrSession: string): boolean {
  try {
    return localStorage.getItem(acceptanceStorageKey(walletOrSession)) === '1'
  } catch {
    return false
  }
}

/** Persist the accepted flag — call ONLY after the server POST returns 2xx. */
export function markAcceptedCurrent(walletOrSession: string): void {
  try {
    localStorage.setItem(acceptanceStorageKey(walletOrSession), '1')
  } catch {
    // If we cannot persist, the user will simply be re-prompted next load (append-only server row).
  }
}

/**
 * Device-scoped suppressor key for the CURRENT versions (Option B, #82). Independent of any wallet:
 * once accepted on this device we suppress the gate for every wallet, while still recording a
 * per-wallet server evidence row silently in the background. A version bump changes the tag → the
 * key changes → the whole device is re-prompted.
 */
export function deviceAcceptanceKey(): string {
  return `parse_terms_accepted_device_${acceptanceVersionTag()}`
}

/** True iff THIS DEVICE has accepted the current versions (any wallet/session). */
export function hasAcceptedDevice(): boolean {
  try {
    return localStorage.getItem(deviceAcceptanceKey()) === '1'
  } catch {
    return false
  }
}

/** Persist the device-level accepted flag — call ONLY after the server POST returns 2xx. */
export function markAcceptedDevice(): void {
  try {
    localStorage.setItem(deviceAcceptanceKey(), '1')
  } catch {
    // If we cannot persist, the user will simply be re-prompted next load (append-only server row).
  }
}

export interface AcceptancePostBody {
  sessionId: string
  walletAddress: string | null
  termsVersion: string
  privacyVersion: string
}

/**
 * POST the acceptance to the server. Resolves true only on a 2xx response; on any non-2xx or
 * network error resolves false so the caller keeps the user gated and offers retry.
 */
export async function postAcceptance(
  apiBase: string,
  sessionId: string,
  walletAddress: string | null,
  headers: Record<string, string>,
): Promise<boolean> {
  try {
    const res = await fetch(`${apiBase}/api/terms/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({
        sessionId,
        walletAddress,
        termsVersion: TERMS_VERSION,
        privacyVersion: PRIVACY_VERSION,
      } satisfies AcceptancePostBody),
    })
    return res.ok
  } catch {
    return false
  }
}
